import { PageRoots, portalPaths, UrlParams, UrlSegments } from '@provision/portal-routes';
import { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { MetaTags } from 'src/components';
import { PartnerGroupRules, UserGroupRules } from 'src/graphql';
import { BusinessHours, Settings, WorkflowsPage } from 'src/pages';
import { BlockedContactsView } from 'src/pages/Settings/blocked-contacts';
import { Channels } from 'src/pages/Settings/channels/channels-view';
import { CompanyInfoView } from 'src/pages/Settings/company-info';
import { DataImport } from 'src/pages/Settings/data-import/data-import.view';
import { EscalationsView } from 'src/pages/Settings/escalations';
import { HolidayScheduleView } from 'src/pages/Settings/holiday-schedule';
import { DeveloperTab } from 'src/pages/Settings/integrations/developer/developer-tab';
import { IntegrationsCardsTab } from 'src/pages/Settings/integrations/integrations-cards-tab';
import { IntegrationsView } from 'src/pages/Settings/integrations/integrations-view';
import { ManageLabels } from 'src/pages/Settings/labels';
import { MembershipsConfigView } from 'src/pages/Settings/memberships/memberships-config-view';
import { NotificationsView } from 'src/pages/Settings/notifications';
import { Preferences } from 'src/pages/Settings/preferences/preferences-view';
import { SignalOnCallSchedule } from 'src/pages/Settings/signal-oncall-schedule/signal-oncall-schedule-view';
import { StripeSettingsView } from 'src/pages/Settings/stripe';
import { Users } from 'src/pages/Settings/users/users-view';
import { ProtectedPage } from 'src/routes/components/protected-page';
import { getRoutePath } from 'src/routes/utils';
import { optionalRouteParam } from 'src/utils/routing';

const RulesEngine = lazy(() => import('src/pages/Settings/rules/rules'));
const Macros = lazy(() => import('src/pages/Settings/macros/macros'));
const ManageCustomAttributes = lazy(
  () => import('src/pages/Settings/custom-attributes/manage-custom-attributes'),
);

export const SettingsRoutes = ({ relative }: { relative?: boolean }) => (
  <Route path={getRoutePath(PageRoots.settings, relative)} element={<Settings />}>
    {/* Company Info - Native Page */}
    <Route
      path={getRoutePath(`${UrlSegments.settings.companyInfo}/*`, relative)}
      element={
        <MetaTags title="Company">
          <CompanyInfoView />
        </MetaTags>
      }
    />

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[PartnerGroupRules.ProvisionSignal, UserGroupRules.ProvisionSignal]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.signal}/*`, relative)}
        element={
          <MetaTags title="Signal">
            <SignalOnCallSchedule />
          </MetaTags>
        }
      />
    </Route>

    <Route
      path={getRoutePath(`${UrlSegments.settings.holidaySchedule}/*`, relative)}
      element={
        <Suspense>
          <MetaTags title="Holiday Schedule">
            <HolidayScheduleView />
          </MetaTags>
        </Suspense>
      }
    />

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToRulesEngine]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.rulesEngine}/*`, relative)}
        element={
          <Suspense>
            <MetaTags title="Rules">
              <RulesEngine />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>
    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[
            PartnerGroupRules.AccessToManageProvisionWorkflows,
            UserGroupRules.AccessToManageProvisionWorkflows,
          ]}
        />
      }
    >
      <Route
        path={getRoutePath(
          `${UrlSegments.settings.workflows}/${optionalRouteParam(
            UrlParams.workflowId,
          )}/${optionalRouteParam(UrlParams.workflowVersionId)}`,
          relative,
        )}
        element={
          <MetaTags title="Workflows">
            <WorkflowsPage />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToDataImport]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.dataImport}/*`, relative)}
        element={
          <MetaTags title="Data Import">
            <DataImport />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToManageBusinessHours]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.businessHours}/*`, relative)}
        element={
          <MetaTags title="Business Hours">
            <BusinessHours />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToManageChannelSettings]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.channels}/*`, relative)}
        element={
          <MetaTags title="Channels">
            <Channels />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToSettingsPreferences]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.preferences}/*`, relative)}
        element={
          <MetaTags title="Preferences">
            <Preferences />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToUserManagementV2]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.users}/*`, relative)}
        element={
          <MetaTags title="Users">
            <Users />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToManageLabels]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.manageLabels}/*`, relative)}
        element={
          <MetaTags title="Labels">
            <ManageLabels />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(UrlSegments.settings.manageCustomAttributes, relative)}
          allowedRules={[UserGroupRules.AccessToManageCustomAttributes]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.manageCustomAttributes}/*`, relative)}
        element={
          <Suspense>
            <MetaTags title="Custom Properties">
              <ManageCustomAttributes />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToManageBlockedContacts]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.blockedContacts}/*`, relative)}
        element={
          <Suspense>
            <MetaTags title="Blocked Contacts">
              <BlockedContactsView />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[
            {
              rules: [
                PartnerGroupRules.AccessToSubscriptionManagement,
                UserGroupRules.AccessToBilling,
              ],
            },
            {
              rules: [
                PartnerGroupRules.AccessToSubscriptionManagement,
                UserGroupRules.AccessToViewMembershipsSettingsPage,
              ],
            },
          ]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.memberships}/*`, relative)}
        element={
          <Suspense>
            <MetaTags title="Memberships Configuration">
              <MembershipsConfigView />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[
            PartnerGroupRules.AccessToSubscriptionManagement,
            UserGroupRules.AccessToBilling,
          ]}
          matchType="all"
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.stripe}/*`, relative)}
        element={
          <MetaTags title="Stripe">
            <StripeSettingsView />
          </MetaTags>
        }
      />
    </Route>

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToManageMacros]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.macros}/*`, relative)}
        element={
          <Suspense>
            <MetaTags title="Macros">
              <Macros />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>

    <Route
      path={getRoutePath('', relative)}
      element={
        <Navigate to={getRoutePath(UrlSegments.settings.companyInfo, relative)} replace />
      }
    />

    <Route
      element={
        <ProtectedPage
          redirectTo={getRoutePath(PageRoots.settings, relative)}
          allowedRules={[UserGroupRules.AccessToManageEscalationSettings]}
        />
      }
    >
      <Route
        path={getRoutePath(`${UrlSegments.settings.signalBusinessHours}/*`, relative)}
        element={
          <Suspense>
            <MetaTags title="Escalation Window">
              <EscalationsView />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>
    <Route
      element={
        <ProtectedPage
          redirectTo={PageRoots.settings}
          allowedRules={[
            UserGroupRules.AccessToNotificationPreferences,
            UserGroupRules.AccessToViewConversations,
          ]}
          matchType="all"
        />
      }
    >
      <Route
        path={`${UrlSegments.settings.notifications}/*`}
        element={
          <Suspense>
            <MetaTags title="Notification Settings">
              <NotificationsView />
            </MetaTags>
          </Suspense>
        }
      />
    </Route>
    <Route
      element={
        <ProtectedPage
          redirectTo={PageRoots.settings}
          allowedRules={[UserGroupRules.AccessToSettingsIntegrations]}
          matchType="all"
        />
      }
    >
      <Route
        path={`${UrlSegments.settings.integrations}/*`}
        element={
          <Suspense>
            <MetaTags title="Integrations">
              <IntegrationsView />
            </MetaTags>
          </Suspense>
        }
      >
        <Route
          element={
            <ProtectedPage
              redirectTo={portalPaths.settings.integrations()}
              allowedRules={[UserGroupRules.AccessToProvisionPublicApi]}
              matchType="all"
            />
          }
        >
          <Route
            path={UrlSegments.settings.integrationsDeveloper}
            element={<DeveloperTab />}
          />
        </Route>
        <Route index element={<IntegrationsCardsTab />} />
      </Route>
    </Route>
  </Route>
);
